/**
 * Keymap for the lesson-note editor.
 *
 * Composes:
 *  - prosemirror-commands base keymap (Enter/Backspace/Delete defaults)
 *  - mark shortcuts (Cmd/Ctrl+B/I/U)
 *  - link shortcut (Cmd/Ctrl+K) — opens a tiny prompt via window.prompt
 *  - list shortcuts (Tab / Shift-Tab indent/outdent, Enter splits)
 *  - Backspace at start of empty heading converts to paragraph
 */
import { keymap } from 'prosemirror-keymap';
import {
  baseKeymap,
  toggleMark,
  setBlockType,
  chainCommands,
  exitCode,
} from 'prosemirror-commands';
import {
  splitListItem,
  liftListItem,
  sinkListItem,
} from 'prosemirror-schema-list';
import type { Schema, MarkType } from 'prosemirror-model';
import type { Command, EditorState, Transaction } from 'prosemirror-state';

/** Cmd+K — open a prompt and set/unset the link mark on the selection. */
function makeToggleLink(linkType: MarkType): Command {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    if (empty) return false;
    // If selection has the mark already, strip it.
    if (state.doc.rangeHasMark(from, to, linkType)) {
      if (dispatch) dispatch(state.tr.removeMark(from, to, linkType));
      return true;
    }
    // window.prompt is synchronous and matches the rest of the codebase's
    // confirm/prompt usage — fancy inline UI lives in the bubble menu instead.
    if (typeof window === 'undefined') return false;
    const href = window.prompt('Enter URL', 'https://');
    if (!href) return true;
    if (dispatch) {
      dispatch(state.tr.addMark(from, to, linkType.create({ href, target: '_blank' })));
    }
    return true;
  };
}

/**
 * Backspace at start of an empty heading converts it to a paragraph. This is a
 * common UX nicety — Notion / Linear / Slate all ship it.
 */
function backspaceHeadingToParagraph(schema: Schema): Command {
  return (state, dispatch) => {
    const { $from, empty } = state.selection;
    if (!empty || $from.parentOffset > 0) return false;
    if ($from.parent.type !== schema.nodes.heading) return false;
    if ($from.parent.content.size > 0) return false;
    if (dispatch) {
      dispatch(state.tr.setBlockType($from.before(), $from.after(), schema.nodes.paragraph));
    }
    return true;
  };
}

/** Soft break on Shift-Enter inside a code block. */
function softBreakInCode(schema: Schema): Command {
  return (state, dispatch) => {
    if (state.selection.$from.parent.type !== schema.nodes.code_block) return false;
    if (dispatch) {
      const br = schema.nodes.hard_break ?? null;
      if (!br) return false;
      const tr: Transaction = state.tr.replaceSelectionWith(br.create()).scrollIntoView();
      dispatch(tr);
    }
    return true;
  };
}

/** Wraps the basic Enter behaviour to first try list-item split. */
function smartEnter(schema: Schema): Command {
  const listItem = schema.nodes.list_item;
  const taskItem = schema.nodes.task_item;
  const cmds: Command[] = [];
  if (listItem) cmds.push(splitListItem(listItem));
  if (taskItem) cmds.push(splitListItem(taskItem));
  cmds.push(exitCode);
  cmds.push(baseKeymap.Enter as Command);
  return chainCommands(...cmds);
}

export function buildKeymap(schema: Schema): ReturnType<typeof keymap> {
  const keys: Record<string, Command> = {};

  const set = (key: string, cmd: Command) => { keys[key] = cmd; };

  // Mark toggles.
  if (schema.marks.strong) {
    set('Mod-b', toggleMark(schema.marks.strong));
    set('Mod-B', toggleMark(schema.marks.strong));
  }
  if (schema.marks.em) {
    set('Mod-i', toggleMark(schema.marks.em));
    set('Mod-I', toggleMark(schema.marks.em));
  }
  if (schema.marks.underline) {
    set('Mod-u', toggleMark(schema.marks.underline));
    set('Mod-U', toggleMark(schema.marks.underline));
  }
  if (schema.marks.code) {
    set('Mod-e', toggleMark(schema.marks.code));
  }
  if (schema.marks.link) {
    set('Mod-k', makeToggleLink(schema.marks.link));
    set('Mod-K', makeToggleLink(schema.marks.link));
  }

  // Heading shortcuts: Cmd+Alt+1/2/3.
  if (schema.nodes.heading) {
    for (let level = 1; level <= 3; level += 1) {
      set(`Mod-Alt-${level}`, setBlockType(schema.nodes.heading, { level }));
    }
  }

  // Paragraph: Cmd+Alt+0.
  if (schema.nodes.paragraph) {
    set('Mod-Alt-0', setBlockType(schema.nodes.paragraph));
  }

  // List indent / outdent. Try list_item first, then task_item.
  const itemTypes = [schema.nodes.list_item, schema.nodes.task_item].filter(Boolean);
  if (itemTypes.length > 0) {
    set('Tab', chainCommands(...itemTypes.map((t) => sinkListItem(t))));
    set('Shift-Tab', chainCommands(...itemTypes.map((t) => liftListItem(t))));
  }

  // Smart enter (split list item / exit code block / fallback).
  set('Enter', smartEnter(schema));

  // Soft-break in code block.
  set('Shift-Enter', softBreakInCode(schema));

  // Backspace on empty heading -> paragraph (then fall through to base).
  const heading2Para = backspaceHeadingToParagraph(schema);
  const baseBackspace = (baseKeymap.Backspace as Command | undefined) ?? ((_state, _dispatch) => false);
  set('Backspace', chainCommands(heading2Para, baseBackspace));

  // Compose with base keymap last so anything we didn't override still works.
  return keymap({ ...baseKeymap, ...keys });
}

export type { EditorState };
